'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { 
  Lock, 
  Shield, 
  Trash2, 
  Eye, 
  EyeOff, 
  AlertTriangle,
  CheckCircle,
  Database,
  Download
} from 'lucide-react'

export default function PrivacySecurityPage() {
  const { t } = useTranslation()
  const { user, userProfile, signOut } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')

  const supabase = createClientComponentClient()

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setMessage(null)

    try {
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setMessage({ type: 'error', text: t('privacy.password.mismatch') })
        return
      }

      if (passwordForm.newPassword.length < 6) {
        setMessage({ type: 'error', text: t('privacy.password.tooShort') })
        return
      }

      // Update password using Supabase Auth
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      })

      if (error) {
        setMessage({ type: 'error', text: error.message })
        return
      }

      setMessage({ type: 'success', text: t('privacy.password.success') })
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
    } catch (error) {
      console.error('Password update error:', error)
      setMessage({ type: 'error', text: t('privacy.password.error') })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAllConversations = async () => {
    if (deleteConfirmText !== t('privacy.confirmations.deleteConversationsConfirm')) {
      setMessage({ type: 'error', text: 'Please type the confirmation text exactly as shown' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      if (!user?.id) {
        setMessage({ type: 'error', text: 'User not found' })
        return
      }

      // First get conversation IDs
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select('id')
        .eq('user_id', user.id)

      if (convError) {
        setMessage({ type: 'error', text: 'Failed to fetch conversations' })
        return
      }

      const conversationIds = conversations?.map((c: any) => c.id) || []

      // Delete conversation states first
      if (conversationIds.length > 0) {
        const { error: statesError } = await supabase
          .from('conversation_states')
          .delete()
          .in('conversation_id', conversationIds)

        if (statesError) {
          console.warn('Some conversation states may not have been deleted:', statesError)
        }

        // Delete all messages
        const { error: messagesError } = await supabase
          .from('messages')
          .delete()
          .in('conversation_id', conversationIds)

        if (messagesError) {
          console.warn('Some messages may not have been deleted:', messagesError)
        }
      }

      // Finally delete conversations
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('user_id', user.id)

      if (error) {
        setMessage({ type: 'error', text: 'Failed to delete conversations' })
        return
      }

      setMessage({ type: 'success', text: t('privacy.confirmations.conversationsDeleted') })
      setShowDeleteConfirm(false)
      setDeleteConfirmText('')
    } catch (error) {
      console.error('Delete conversations error:', error)
      setMessage({ type: 'error', text: t('privacy.confirmations.conversationsError') })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== t('privacy.confirmations.deleteAccountConfirm')) {
      setMessage({ type: 'error', text: 'Please type the confirmation text exactly as shown' })
      return
    }

    setIsLoading(true)
    setMessage(null)

    try {
      // Call the delete account edge function
      const { error } = await supabase.functions.invoke('delete-account', {
        body: { userId: user?.id }
      })

      if (error) {
        setMessage({ type: 'error', text: 'Failed to delete account' })
        return
      }

      // Sign out the user
      await signOut()
    } catch (error) {
      console.error('Delete account error:', error)
      setMessage({ type: 'error', text: t('privacy.confirmations.accountError') })
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportData = async () => {
    setIsLoading(true)
    setMessage(null)

    try {
      if (!user?.id) {
        setMessage({ type: 'error', text: 'User not found' })
        return
      }

      // First get conversations
      const conversationsResult = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', user.id)

      const conversationIds = conversationsResult.data?.map((c: any) => c.id) || []

      // Then get messages for those conversations
      const messagesData: any[] = []
      if (conversationIds.length > 0) {
        const messagesResult = await supabase
          .from('messages')
          .select('*')
          .in('conversation_id', conversationIds)
        
        messagesData.push(...(messagesResult.data || []))
      }

      // Get user memories
      const userMemoriesResult = await supabase
        .from('user_memories')
        .select('*')
        .eq('user_id', user.id)

      const exportData = {
        user: {
          id: user?.id,
          email: user?.email,
          profile: userProfile
        },
        conversations: conversationsResult.data || [],
        messages: messagesData,
        memories: userMemoriesResult.data || [],
        exportDate: new Date().toISOString()
      }

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `sage-data-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setMessage({ type: 'success', text: t('privacy.dataManagement.exportSuccess') })
    } catch (error) {
      console.error('Export data error:', error)
      setMessage({ type: 'error', text: t('privacy.dataManagement.exportError') })
    } finally {
      setIsLoading(false)
    }
  }

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-800 mb-4 flex items-center justify-center">
            <Shield className="w-10 h-10 mr-3 text-emerald-400" />
            {t('privacy.title')}
          </h1>
          <p className="text-xl text-slate-400">
            {t('privacy.subtitle')}
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <AlertTriangle className="w-5 h-5 mr-2" />
            )}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Password Change */}
          <Card className="p-8">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6 flex items-center">
              <Lock className="w-6 h-6 mr-2 text-emerald-400" />
              {t('privacy.password.title')}
            </h2>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('privacy.password.newPasswordLabel')}
                </label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder={t('privacy.password.newPasswordPlaceholder')}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-400"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  {t('privacy.password.confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder={t('privacy.password.confirmPasswordPlaceholder')}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-400"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
              >
                {isLoading ? t('privacy.password.updating') : t('privacy.password.updateButton')}
              </Button>
            </form>
          </Card>

          {/* Data Management */}
          <Card className="p-8">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6 flex items-center">
              <Database className="w-6 h-6 mr-2 text-emerald-400" />
              {t('privacy.dataManagement.title')}
            </h2>
            
            <div className="space-y-4">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleExportData}
                disabled={isLoading}
              >
                <Download className="w-4 h-4 mr-2" />
                {t('privacy.dataManagement.exportData')}
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isLoading}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('privacy.dataManagement.deleteConversations')}
              </Button>
            </div>
          </Card>

          {/* Account Deletion */}
          <Card className="p-8 lg:col-span-2">
            <h2 className="text-2xl font-semibold text-slate-800 mb-6 flex items-center">
              <AlertTriangle className="w-6 h-6 mr-2 text-red-600" />
              {t('privacy.accountDeletion.title')}
            </h2>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-800 text-sm">
                <strong>Warning:</strong> {t('privacy.accountDeletion.warning')}
              </p>
            </div>

            <Button 
              variant="outline" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('privacy.accountDeletion.deleteAccount')}
            </Button>
          </Card>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
              <h3 className="text-xl font-semibold text-slate-800 mb-4">
                {t('privacy.confirmations.title')}
              </h3>
              
              <p className="text-slate-400 mb-6">
                {t('privacy.confirmations.description')}
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('privacy.confirmations.typeDeleteConversations')}
                  </label>
                  <Input
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder={t('privacy.confirmations.placeholder')}
                  />
                </div>

                <div className="flex space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteConfirm(false)
                      setDeleteConfirmText('')
                    }}
                    className="flex-1"
                  >
                    {t('privacy.confirmations.cancel')}
                  </Button>
                  
                  <Button 
                    onClick={deleteConfirmText === t('privacy.confirmations.deleteConversationsConfirm') ? handleDeleteAllConversations : handleDeleteAccount}
                    disabled={isLoading || !deleteConfirmText}
                    className={`flex-1 ${
                      deleteConfirmText === t('privacy.confirmations.deleteAccountConfirm') 
                        ? 'bg-red-600 hover:bg-red-700' 
                        : 'bg-orange-600 hover:bg-orange-700'
                    }`}
                  >
                    {isLoading ? t('privacy.confirmations.deleting') : t('privacy.confirmations.confirmDelete')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
